import { useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Lock, ChevronDown, LogOut, User, Coins, Gift } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "./Logo";
import { useAuth } from "../context/AuthContext";

const NAV = [
  { to: "/firms", label: "Firms" },
  { to: "/reviews", label: "Reviews" },
  { to: "/compare", label: "Compare" },
  { to: "/blog", label: "Articles" },
  { to: "/about", label: "About" },
];

const TOOLS = [
  { to: "/journal", label: "Trading Journal" },
  { to: "/tools/consistency", label: "Consistency Calculator" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const goFaq = () => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById("faq")?.scrollIntoView({ behavior: "smooth" }), 400);
    } else {
      document.getElementById("faq")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success("SIGNED OUT");
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 border-b border-line bg-ink/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 lg:px-8">
        <Link to="/" data-testid="header-home-link" onClick={() => setOpen(false)}>
          <Logo />
        </Link>
        <nav className="hidden items-center gap-7 md:flex" data-testid="header-nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-200 ${
                  isActive ? "text-signal" : "text-muted hover:text-white"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <div className="group relative">
            <button data-testid="nav-tools" className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.2em] text-muted transition-colors duration-200 hover:text-white">
              Tools <ChevronDown size={11} className="transition-transform duration-200 group-hover:rotate-180" />
            </button>
            <div className="invisible absolute left-1/2 top-full -translate-x-1/2 pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
              <div className="w-56 rounded-2xl border border-line bg-surface p-2 shadow-[0_16px_50px_rgba(0,0,0,0.5)]">
                {TOOLS.map((t) => (
                  <Link key={t.to} to={t.to} data-testid={`nav-tool-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="block rounded-xl px-4 py-2.5 text-sm text-muted transition-colors hover:bg-signal/10 hover:text-white">
                    {t.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
          <button onClick={goFaq} data-testid="nav-faq" className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted transition-colors duration-200 hover:text-white">
            FAQ
          </button>
          <button onClick={() => navigate(user ? "/wallet" : "/auth?next=/wallet")} data-testid="nav-wallet-btn"
            className="inline-flex items-center gap-1.5 rounded-full border border-gold/40 bg-gold/10 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-gold transition-colors hover:border-gold/70">
            <Coins size={11} /> Wallet
          </button>
          {user ? (
            <span className="flex items-center gap-3" data-testid="header-user">
              <button onClick={() => navigate("/earn")} data-testid="nav-earn-btn"
                className="inline-flex items-center gap-1.5 rounded-full border border-line px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-muted transition-colors hover:border-signal/60 hover:text-signal">
                <Gift size={11} /> Earn
              </button>
              {user.role === "admin" && (
                <button onClick={() => navigate("/admin")} data-testid="nav-admin-btn"
                  className="inline-flex items-center gap-1.5 rounded-full border border-line px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-muted transition-colors hover:border-signal/60 hover:text-signal">
                  <Lock size={11} /> Dashboard
                </button>
              )}
              <button onClick={() => navigate(`/members/${user.id}`)} title="View my profile" data-testid="nav-profile-chip"
                className="inline-flex items-center gap-2 rounded-full border border-signal/40 bg-signal/10 px-4 py-1.5 transition-colors hover:border-signal/70">
                {user.picture ? (
                  <img src={user.picture} alt="" className="h-5 w-5 rounded-full" />
                ) : (
                  <User size={12} className="text-signal" />
                )}
                <span className="max-w-[110px] truncate font-mono text-[10px] font-600 uppercase tracking-[0.1em] text-white">{user.name}</span>
              </button>
              <button onClick={handleLogout} data-testid="nav-logout-btn" aria-label="Log out"
                className="text-muted transition-colors hover:text-loss">
                <LogOut size={15} />
              </button>
            </span>
          ) : (
            <button
              data-testid="nav-admin-btn"
              onClick={() => navigate("/auth")}
              className="btn-gradient inline-flex items-center gap-1.5 px-5 py-2 font-mono text-[10px] font-700 uppercase tracking-[0.18em] text-white"
            >
              <Lock size={11} /> Login
            </button>
          )}
        </nav>
        <button
          className="text-white md:hidden"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div className="border-t border-line bg-ink px-5 py-5 md:hidden" data-testid="mobile-menu">
          <div className="grid gap-2.5">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className="rounded-xl border border-line bg-surface px-5 py-3.5 text-center font-display text-sm font-700 text-white"
              >
                {item.label}
              </NavLink>
            ))}
            {TOOLS.map((t) => (
              <NavLink key={t.to} to={t.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="rounded-xl border border-line bg-surface px-5 py-3.5 text-center font-display text-sm font-700 text-white">
                {t.label}
              </NavLink>
            ))}
            <button onClick={goFaq} data-testid="mobile-nav-faq" className="rounded-xl border border-line bg-surface px-5 py-3.5 font-display text-sm font-700 text-white">
              FAQ
            </button>
            <button onClick={() => { setOpen(false); navigate(user ? "/wallet" : "/auth?next=/wallet"); }} data-testid="mobile-nav-wallet"
              className="rounded-xl border border-gold/40 bg-gold/10 px-5 py-3.5 font-display text-sm font-700 text-gold">
              FTC Wallet
            </button>
            {user ? (
              <>
                <button onClick={() => { setOpen(false); navigate("/earn"); }} data-testid="mobile-nav-earn"
                  className="rounded-xl border border-line bg-surface px-5 py-3.5 font-display text-sm font-700 text-white">
                  Refer & Earn
                </button>
                {user.role === "admin" && (
                  <button onClick={() => { setOpen(false); navigate("/admin"); }} data-testid="mobile-nav-admin"
                    className="rounded-xl border border-signal/50 bg-signal/10 px-5 py-3.5 font-display text-sm font-700 text-signal">
                    Admin Dashboard
                  </button>
                )}
                <button onClick={() => { setOpen(false); handleLogout(); }} data-testid="mobile-nav-logout"
                  className="rounded-xl border border-line bg-surface px-5 py-3.5 font-display text-sm font-700 text-muted">
                  Sign Out ({user.name})
                </button>
              </>
            ) : (
              <button
                onClick={() => { setOpen(false); navigate("/auth"); }}
                data-testid="mobile-nav-admin"
                className="btn-gradient px-5 py-3.5 font-display text-sm font-700 text-white"
              >
                Member Login
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
